import { mkFcToggle } from './form-controls.js';

// ---------------------------------------------------------------------------
// column-picker.ts — THE COLUMN PICKER POPOVER, one primitive for every grid
// that owns a trigger + popover pair: the four console grids (home, users,
// audit, config-templates via admin-grid) AND the Helm's glance grid
// (glance.ts, "Show columns"). It lives in pi-stack/src/client because both
// hosts mount it — the same reason buttons.ts and form-controls.ts do.
//
// Owner screenshot 2026-09-08: the toggle list rendered rows JAMMED TWO TO A
// LINE ("Name [toggle] ID", "Status [toggle] Fleet" …), stood taller than the
// viewport and clipped at the bottom with no scroll. Measured cause: the rows
// are `mkFcToggle` buttons (`.fc-tg`, display: inline-flex) appended straight
// into a BLOCK container — inline boxes flow side by side whenever a label is
// short — and the menu's only height bound was `max-height: 60vh` measured
// from wherever the toolbar happened to sit, not from the viewport's edge.
// home.ts and admin-grid.ts each carried their own copy of the paint + open +
// dismiss wiring, so a fix in one would have left the other three grids
// broken. This is the one copy now.
//
// Contract (proved by scripts/column-picker.test.mjs on the stub DOM and by
// e2e/style-parity-probe.mjs's `popover` arm in a real browser):
//   * paint(): ONE row per entry, each a direct child of the menu, each the
//     toggle primitive (role=switch) carrying exactly that entry's label;
//   * open(): paints, unhides, and bounds max-height to the VIEWPORT below the
//     trigger (viewport height - trigger bottom - gap), never a bare 60vh —
//     the container's CSS is a single flex column with overflow-y: auto, and
//     the themed rail draws because `.hc-colsmenu` is a declared surface in
//     rail-registry.ts (B12);
//   * dismiss on outside click and Escape; destroy() removes both listeners.
// ---------------------------------------------------------------------------

export interface ColumnPickerEntry { colId: string; label: string; visible: boolean }

export interface ColumnPickerOpts {
  btn: HTMLButtonElement;
  menu: HTMLElement;
  /** Re-read on every open — the grid's live column state wins. */
  entries: () => ColumnPickerEntry[];
  onToggle: (colId: string, on: boolean) => void;
  /** Breathing room kept between the menu's bottom and the viewport's (px). */
  gap?: number;
  /** An eyebrow row painted above the toggles ("Show columns" on the Helm). */
  title?: { text: string; cls: string };
  /** LAYOUT class handed to every toggle row (the Helm's `gl-colrow`). */
  rowCls?: string;
  /** Runs before the menu opens — the glance grid closes its other popovers. */
  onOpen?: () => void;
  /** Open-state mirror for the trigger (the glance button toggles `.on`). */
  onOpenChange?: (open: boolean) => void;
}

export interface ColumnPickerCtl {
  paint(): void;
  open(): void;
  close(): void;
  toggle(): void;
  destroy(): void;
}

/** The smallest useful popover — below this the bound is not applied, so a
 *  trigger sitting at the very bottom of a tiny viewport still opens. */
export const COLUMN_PICKER_MIN_HEIGHT = 120;

export function mountColumnPicker(o: ColumnPickerOpts): ColumnPickerCtl {
  const { btn, menu } = o;
  const gap = o.gap ?? 12;
  const doc = (menu.ownerDocument ?? document) as Document;

  function paint(): void {
    menu.innerHTML = '';
    if (o.title) {
      const t = doc.createElement('div');
      t.className = o.title.cls;
      t.textContent = o.title.text;
      menu.appendChild(t);
    }
    for (const e of o.entries()) {
      // A column-visibility row is a binary that commits immediately, not a
      // bulk row-select — the ruling's checkbox carve-out does not reach it,
      // so it is a toggle. ONE row per entry, appended as a direct child: the
      // container's flex column (styles.css .hc-colsmenu) is what keeps each
      // on its own line.
      const tg = mkFcToggle({
        label: e.label,
        checked: e.visible,
        cls: o.rowCls,
        onChange: (on) => o.onToggle(e.colId, on),
      });
      menu.appendChild(tg.el);
    }
  }

  /** Bound the menu to the viewport BELOW the trigger. Both reads are
   *  optional-chained so the stub DOM (no layout) mounts without them. */
  function bound(): void {
    const win = doc.defaultView ?? (typeof window !== 'undefined' ? window : null);
    const vh = win?.innerHeight;
    const rect = btn.getBoundingClientRect?.();
    if (!vh || !rect) return;
    const top = rect.bottom + 6; // mirrors .hc-colsmenu / .gl-colpop { top: calc(100% + 6px) }
    let max = Math.floor(vh - top - gap);
    // The sheet's OWN cap still counts (the Helm's .gl-colpop is 340px by
    // design, the console's .hc-colsmenu 60vh): the viewport bound only ever
    // TIGHTENS it. Read with the inline value cleared so the cascade answers.
    if (win?.getComputedStyle) {
      menu.style.maxHeight = '';
      const sheet = Number.parseFloat(win.getComputedStyle(menu).maxHeight);
      if (Number.isFinite(sheet) && sheet > 0) max = Math.min(max, Math.floor(sheet));
    }
    menu.style.maxHeight = max >= COLUMN_PICKER_MIN_HEIGHT ? `${max}px` : '';
  }

  function open(): void {
    o.onOpen?.();
    paint();
    menu.hidden = false;
    bound();
    o.onOpenChange?.(true);
  }
  function close(): void {
    if (menu.hidden) return;
    menu.hidden = true;
    o.onOpenChange?.(false);
  }
  function toggle(): void { if (menu.hidden) open(); else close(); }

  const onBtn = (ev: Event): void => { ev.stopPropagation(); toggle(); };
  // `contains` when the DOM has it; otherwise walk parents — the stub DOM
  // (scripts/client-dom-stub.mjs) links children through `parent`, and a
  // primitive that closes on every inside click there is untestable.
  const within = (root: Node, t: Node | null): boolean => {
    if (!t) return false;
    if (typeof (root as HTMLElement).contains === 'function') return (root as HTMLElement).contains(t);
    for (let n: unknown = t; n; n = (n as { parentNode?: unknown; parent?: unknown }).parentNode ?? (n as { parent?: unknown }).parent) {
      if (n === root) return true;
    }
    return false;
  };
  const onDocClick = (ev: Event): void => {
    if (menu.hidden) return;
    const t = ev.target as Node | null;
    if (within(menu, t) || within(btn, t)) return;
    close();
  };
  const onKey = (ev: KeyboardEvent): void => { if (ev.key === 'Escape' && !menu.hidden) close(); };
  btn.addEventListener('click', onBtn);
  doc.addEventListener('click', onDocClick);
  doc.addEventListener('keydown', onKey);

  return {
    paint, open, close, toggle,
    destroy() {
      btn.removeEventListener?.('click', onBtn);
      doc.removeEventListener('click', onDocClick);
      doc.removeEventListener('keydown', onKey);
    },
  };
}
